import { useEffect, useRef, useState, useCallback } from "react";
import { Play, Pause, RotateCcw, Music, Clock, CheckCircle } from "lucide-react";

interface ChordEvent {
  chord: string;
  durationBeats: number;
}

interface YouTubeSyncProps {
  youtubeUrl: string;
  chordSequence: ChordEvent[];
  bpm: number;
  onTimingsComputed: (timings: number[], timestamps: number[]) => void;
  initialTimestamps?: number[];
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number; BUFFERING: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  destroy: () => void;
}

export function YouTubeSync({ youtubeUrl, chordSequence, bpm, onTimingsComputed, initialTimestamps }: YouTubeSyncProps) {
  const videoId = extractYouTubeId(youtubeUrl);
  const playerRef = useRef<YTPlayer | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [marks, setMarks] = useState<number[]>(initialTimestamps || []);
  const [activeChordIndex, setActiveChordIndex] = useState(-1);
  const rafRef = useRef<number>(0);

  const initPlayer = useCallback(() => {
    if (!videoId || !window.YT) return;
    playerRef.current = new window.YT.Player("youtube-player", {
      videoId,
      playerVars: {
        autoplay: 0,
        controls: 1,
        modestbranding: 1,
        rel: 0,
      },
      events: {
        onReady: (e) => {
          setIsReady(true);
          setDuration(e.target.getDuration());
        },
        onStateChange: (e) => {
          const ps = window.YT!.PlayerState;
          if (e.data === ps.BUFFERING) return;
          setIsPlaying(e.data === ps.PLAYING);
        },
      },
    });
  }, [videoId]);

  const lastComputedMarksRef = useRef<number[]>([]);

  // Sincronizar marcas con timestamps guardados cuando cambian
  useEffect(() => {
    if (!initialTimestamps || initialTimestamps.length === 0) return;
    // Solo actualizar si son realmente diferentes de las marcas actuales
    const current = marks;
    if (initialTimestamps.length !== current.length || initialTimestamps.some((t, i) => t !== current[i])) {
      setMarks(initialTimestamps);
      lastComputedMarksRef.current = initialTimestamps;
    }
  }, [initialTimestamps]);

  // Calcular y guardar automáticamente cada vez que cambian las marcas
  useEffect(() => {
    if (marks.length === 0 || bpm <= 0) return;
    // Evitar loop infinito: solo computar si las marcas realmente cambiaron
    const last = lastComputedMarksRef.current;
    if (marks.length === last.length && marks.every((t, i) => t === last[i])) return;

    const beatDuration = 60 / bpm;
    const timings: number[] = [];
    const timestamps: number[] = [];

    for (let i = 0; i < chordSequence.length && i < marks.length; i++) {
      const start = marks[i];
      const end = i + 1 < marks.length ? marks[i + 1] : start + 4 * beatDuration;
      const durationSec = end - start;
      const beats = Math.round(durationSec / beatDuration);
      timings.push(Math.max(1, beats));
      timestamps.push(start);
    }

    lastComputedMarksRef.current = [...marks];
    onTimingsComputed(timings, timestamps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marks, bpm, chordSequence.length]);

  // Cargar API de YouTube
  useEffect(() => {
    if (!videoId) return;
    if (window.YT && window.YT.Player) {
      initPlayer();
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode?.insertBefore(tag, firstScript);
    window.onYouTubeIframeAPIReady = () => {
      initPlayer();
    };
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  // Loop de tiempo actual
  useEffect(() => {
    if (!isPlaying) {
      cancelAnimationFrame(rafRef.current);
      return;
    }
    const update = () => {
      if (playerRef.current) {
        const t = playerRef.current.getCurrentTime();
        setCurrentTime(t);
        // Determinar acorde activo basado en marcas
        if (marks.length > 0) {
          let idx = 0;
          for (let i = 0; i < marks.length; i++) {
            if (t >= marks[i]) idx = i;
          }
          setActiveChordIndex(Math.min(idx, chordSequence.length - 1));
        }
      }
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying, marks, chordSequence.length]);

  // Tecla espacio para marcar
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      e.preventDefault();
      if (!playerRef.current) return;
      const t = playerRef.current.getCurrentTime();
      setMarks((prev) => {
        const next = [...prev, t].sort((a, b) => a - b);
        return next;
      });
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const togglePlay = useCallback(() => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, [isPlaying]);

  const resetMarks = useCallback(() => {
    setMarks([]);
    setActiveChordIndex(-1);
  }, []);

  const removeMark = useCallback((index: number) => {
    setMarks((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const computeTimings = useCallback(() => {
    if (marks.length === 0 || bpm <= 0) return;
    const beatDuration = 60 / bpm;
    const timings: number[] = [];
    const timestamps: number[] = [];

    for (let i = 0; i < chordSequence.length && i < marks.length; i++) {
      const start = marks[i];
      const end = i + 1 < marks.length ? marks[i + 1] : start + 4 * beatDuration;
      const durationSec = end - start;
      const beats = Math.round(durationSec / beatDuration);
      timings.push(Math.max(1, beats));
      timestamps.push(start);
    }

    onTimingsComputed(timings, timestamps);
  }, [marks, bpm, chordSequence.length, onTimingsComputed]);

  if (!videoId) {
    return (
      <div className="text-[11px] text-[#B8A99A] text-center py-4">
        Ingresa una URL de YouTube válida para sincronizar
      </div>
    );
  }

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div ref={containerRef} className="space-y-3">
      {/* Reproductor */}
      <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "16/9" }}>
        <div id="youtube-player" className="w-full h-full" />
        {!isReady && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-xs">
            Cargando video...
          </div>
        )}
      </div>

      {/* Controles */}
      <div className="flex items-center gap-2">
        <button
          onClick={togglePlay}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors ${
            isPlaying
              ? "bg-[#EF4444] text-white"
              : "bg-[#10B981] text-white"
          }`}
        >
          {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {isPlaying ? "Pausar" : "Reproducir"}
        </button>
        <button
          onClick={resetMarks}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium bg-[#F5F0E8] text-[#5A524A] hover:bg-[#E8E0D4] transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Reset
        </button>
        <div className="flex-1 text-[10px] text-[#8A8078] text-right">
          <Clock className="w-3 h-3 inline mr-1" />
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>

      {/* Instrucciones */}
      <div className="text-[10px] text-[#8A8078] bg-[#F5F0E8] rounded-lg px-3 py-2">
        <span className="font-semibold text-[#7B2335]">Espacio</span> = marcar cambio de acorde.
        Toca Espacio justo cuando cambie el acorde en la canción.
      </div>

      {/* Marcas y acordes */}
      <div className="space-y-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-[#8A8078]">
          Marcas ({marks.length} / {chordSequence.length})
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chordSequence.map((event, i) => {
            const hasMark = i < marks.length;
            const isActive = i === activeChordIndex;
            return (
              <button
                key={i}
                onClick={() => {
                  if (hasMark && playerRef.current) {
                    playerRef.current.seekTo(marks[i], true);
                  }
                }}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium border transition-all ${
                  isActive
                    ? "bg-[#7B2335] text-white border-[#7B2335]"
                    : hasMark
                    ? "bg-[#10B981]/10 text-[#059669] border-[#10B981]/30"
                    : "bg-white text-[#8A8078] border-[#E8E0D4]"
                }`}
              >
                <Music className="w-3 h-3" />
                {event.chord}
                {hasMark && (
                  <span className="text-[9px] opacity-70">{formatTime(marks[i])}</span>
                )}
                {hasMark && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMark(i);
                    }}
                    className="ml-0.5 text-[9px] hover:text-red-500 cursor-pointer"
                  >
                    ×
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Estado de guardado automático */}
      {marks.length > 0 && (
        <div className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium bg-[#10B981]/10 text-[#059669]">
          <CheckCircle className="w-3.5 h-3.5" />
          Duraciones calculadas automáticamente ({marks.length} marcas)
        </div>
      )}
    </div>
  );
}
